import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface ValueCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  index?: number;
}

/**
 * ValueCard Component
 * 
 * A visually appealing card component that displays a value with an icon, title, and description.
 * Features scroll-triggered animations that are performance-optimized for mobile devices.
 */
const ValueCard: React.FC<ValueCardProps> = ({ 
  icon: Icon, 
  title, 
  description, 
  index = 0 
}) => {
  // Detect mobile for responsive animations
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Staggered animation delay based on index
  const staggerDelay = isMobile ? 0 : index * 0.1;
  
  // Simpler animation variants for better performance
  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: isMobile ? 10 : 20 // Reduced movement on mobile
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: isMobile ? 0.4 : 0.6,
        delay: staggerDelay,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-10%" }}
    >
      <div className="bg-purple-100 dark:bg-purple-800 rounded-full w-12 h-12 flex items-center justify-center mb-4" aria-hidden="true">
        <Icon className="h-6 w-6 text-purple-600 dark:text-purple-300" />
      </div>
      <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
        {title}
      </h3>
      <p className="text-gray-600 dark:text-gray-300">
        {description}
      </p>
    </motion.div>
  );
};

export default ValueCard;
